/**
 * Marketplace API client — thin wrappers over the gateway endpoints.
 *
 * Replaces the old localStorage-based mock. The catalog is fetched from
 * the server (``GET /v1/marketplace/catalog``), and install state is
 * persisted per-team in Postgres.
 */

import { api } from "@/lib/api";
import type { MarketplaceItem } from "@/lib/types";

// ────────────────────────────────────────────────────────────────────
// Wire types (match the Pydantic response schemas in marketplace.py)
// ────────────────────────────────────────────────────────────────────

interface PackResponse {
  id: string;
  kind: string;
  name: string;
  tagline: string;
  description: string;
  category: string;
  bundles: string[];
  risk: string;
  version: string;
  vendor: string;
  kb_slug?: string | null;
  install_deps?: string[];
}

interface CatalogResponse {
  items: PackResponse[];
}

interface InstallRowWire {
  id: string;
  pack_id: string;
  kind: string;
  version: string;
  provisioned_resource_id: string | null;
  installed_by_user_id: string;
  created_at: string;
  updated_at: string;
}

interface InstalledResponse {
  items: InstallRowWire[];
}

interface InstallResult {
  install: InstallRowWire;
  provisioned_resource_id: string | null;
  auto_installed_skills: string[];
  message: string;
}

// ────────────────────────────────────────────────────────────────────
// Converters
// ────────────────────────────────────────────────────────────────────

function packToMarketplaceItem(p: PackResponse): MarketplaceItem {
  return {
    id: p.id,
    kind: p.kind as MarketplaceItem["kind"],
    name: p.name,
    tagline: p.tagline,
    description: p.description,
    category: p.category,
    bundles: p.bundles,
    risk: (p.risk ?? "safe") as MarketplaceItem["risk"],
    version: p.version,
    vendor: p.vendor,
    kb_slug: p.kb_slug ?? undefined,
  };
}

// ────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────

/** Fetch the full platform catalog. */
export async function fetchCatalog(): Promise<MarketplaceItem[]> {
  const res = await api<CatalogResponse>("/marketplace/catalog");
  return res.items.map(packToMarketplaceItem);
}

/** Fetch the current team's installed pack IDs. */
export async function fetchInstalledIds(): Promise<string[]> {
  const res = await api<InstalledResponse>("/marketplace/installed");
  return res.items.map((r) => r.pack_id);
}

/** Install a marketplace pack. Returns the install result. */
export async function installPack(packId: string): Promise<InstallResult> {
  return api<InstallResult>(`/marketplace/install/${encodeURIComponent(packId)}`, {
    method: "POST",
  });
}

/** Uninstall a marketplace pack. */
export async function uninstallPack(packId: string): Promise<void> {
  await api<void>(`/marketplace/installed/${encodeURIComponent(packId)}`, {
    method: "DELETE",
  });
}
